import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import { Check, ChevronRight, Copy, LifeBuoy, X } from 'lucide-react';
import type { SupportTicketCreated, SupportTicketDetail } from '@spawnwire/shared';
import { Wordmark } from '../../components/Logo';
import { Button, clsx, EmptyState, IconButton, Spinner } from '../../components/ui';
import { api, errorMessage, http } from '../../lib/api';
import { copyText } from '../../lib/hooks';
import { savedTickets } from '../../lib/local';
import { publicOrigin } from '../../lib/platform';
import { BackLink, Conversation, PersonReply, TicketForm, TicketHeader } from './TicketParts';

/** Signed out, replies arrive only on the ticket's page, so it checks for them now and then. */
const REFRESH_MS = 60_000;

function ContactForm() {
  const navigate = useNavigate();
  const [saved, setSaved] = useState(() => savedTickets.get());
  return (
    <>
      <h1 className="text-3xl font-bold tracking-tight">Contact support</h1>
      <p className="mt-2 text-[15px] leading-7 text-muted">
        Can't get into your account, or need help with something else? Send us a message. You don't need to be signed in: give the email address your account is registered with
        so we can find it.
      </p>
      <p className="mt-2 text-sm text-muted">
        Can you sign in?{' '}
        <Link to="/login?next=%2Fapp%2Fsupport" className="text-link hover:underline">
          Sign in
        </Link>{' '}
        and open Support from the home sidebar, so your tickets are kept on your account.
      </p>

      {saved.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Tickets from this device</h2>
          <ul className="space-y-2">
            {saved.map((t) => (
              <li key={t.id} className="flex items-center gap-1 rounded-xl border border-line bg-sidebar pr-2">
                <Link to={`/support/ticket/${t.id}#${t.key}`} className="flex min-w-0 flex-1 items-center gap-3 rounded-l-xl p-4 hover:bg-elevated">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold text-fg">{t.subject}</span>
                    <span className="block text-sm text-muted">Ticket #{t.number}</span>
                  </span>
                  <ChevronRight className="size-5 shrink-0 text-faint" aria-hidden />
                </Link>
                <IconButton
                  icon={X}
                  size="sm"
                  label="Forget on this device"
                  onClick={() => {
                    savedTickets.remove(t.id);
                    setSaved(savedTickets.get());
                  }}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-8 rounded-2xl border border-line bg-main p-5 sm:p-6">
        <h2 className="mb-4 text-lg font-semibold text-fg">New ticket</h2>
        <TicketForm
          askEmail
          defaultCategory="account"
          onSubmit={async (values) => {
            const { ticket, accessKey } = await http.post<SupportTicketCreated>('/support/tickets', values);
            if (accessKey) savedTickets.save({ id: ticket.id, number: ticket.number, subject: ticket.subject, key: accessKey });
            navigate(`/support/ticket/${ticket.id}#${accessKey ?? ''}`, { state: { created: true } });
          }}
        />
      </section>
    </>
  );
}

function KeepLink({ created }: { created: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className={clsx('rounded-xl border p-4', created ? 'border-accent/40 bg-accent-soft' : 'border-line bg-sidebar')}>
      <p className="font-semibold text-fg">{created ? 'Ticket sent. Keep this link.' : 'Keep this link to come back'}</p>
      <p className="mt-1 text-sm leading-relaxed text-muted">
        While you're signed out, this page is the only place our reply shows up. It's saved on this device, but bookmark it or copy the link somewhere safe too. Anyone with the
        link can read this ticket, so don't share it.
      </p>
      <Button
        size="sm"
        variant="secondary"
        icon={copied ? Check : Copy}
        className="mt-3"
        onClick={() =>
          void copyText(`${publicOrigin()}${location.pathname}${location.search}${location.hash}`).then(
            () => setCopied(true),
            () => setCopied(false),
          )
        }
      >
        {copied ? 'Copied' : 'Copy link'}
      </Button>
    </div>
  );
}

function GuestTicket({ signedIn }: { signedIn: boolean }) {
  const { ticketId = '' } = useParams();
  const { hash, state } = useLocation();
  const key = decodeURIComponent(hash.slice(1));
  const [ticket, setTicket] = useState<SupportTicketDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const call = useCallback(
    (method: 'GET' | 'POST', path = '', body?: unknown) => api<SupportTicketDetail>(method, `/support/tickets/${ticketId}${path}`, body, { headers: { 'x-support-key': key } }),
    [ticketId, key],
  );

  useEffect(() => {
    if (!key) return;
    const load = () =>
      call('GET').then(
        (t) => {
          setTicket(t);
          setError(null);
          savedTickets.save({ id: t.id, number: t.number, subject: t.subject, key });
        },
        (err) => setError(errorMessage(err)),
      );
    void load();
    const timer = setInterval(() => document.visibilityState === 'visible' && void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [call, key]);

  // Your own ticket, opened without its key: it lives in the app.
  if (!key && signedIn) return <Navigate to={`/app/support/${ticketId}`} replace />;
  if (!key || (error && !ticket)) {
    return (
      <EmptyState
        icon={LifeBuoy}
        title="We couldn't open this ticket"
        body="The link may be incomplete. Open it exactly as you saved it, including everything after the # sign."
        action={
          <Link to="/support" className="text-sm font-medium text-link hover:underline">
            Contact support
          </Link>
        }
      />
    );
  }
  if (!ticket) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-8" />
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <BackLink to="/support">Contact support</BackLink>
      <KeepLink created={!!(state as { created?: boolean } | null)?.created} />
      <TicketHeader ticket={ticket} />
      <Conversation messages={ticket.messages} who={(m) => (m.fromStaff ? 'Spawnwire Support' : 'You')} />
      <PersonReply ticket={ticket} send={async (action, body) => setTicket(await call('POST', `/${action}`, body ?? {}))} />
    </div>
  );
}

/**
 * The public support page at /support: anyone can open a ticket without signing in, and come back to it through
 * its private link. Signed-in people are sent to Support in the app instead.
 */
export function PublicSupport({ signedIn }: { signedIn: boolean }) {
  const { pathname } = useLocation();
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Support · Spawnwire';
    return () => {
      document.title = previous;
    };
  }, []);

  // Sending the form opens the ticket at the top, not scrolled to where the button was.
  useEffect(() => {
    scroller.current?.scrollTo(0, 0);
  }, [pathname]);

  if (signedIn && /^\/support\/?$/.test(pathname)) return <Navigate to="/app/support" replace />;
  return (
    <div ref={scroller} className="h-full overflow-y-auto bg-rail text-fg scroll-thin">
      <header className="border-b border-line bg-sidebar/60">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <Link to="/" aria-label="Spawnwire">
            <Wordmark />
          </Link>
          <Link to={signedIn ? '/app/support' : '/login'} className="shrink-0 rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-accent-fg hover:bg-accent-hover">
            {signedIn ? 'Open Spawnwire' : 'Sign in'}
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
        <Routes>
          <Route index element={<ContactForm />} />
          <Route path="ticket/:ticketId" element={<GuestTicket signedIn={signedIn} />} />
          <Route path="*" element={<Navigate to="/support" replace />} />
        </Routes>
      </main>
    </div>
  );
}
